import DocumentFieldGrid from './DocumentFieldGrid'
import DocumentSection from './DocumentSection'
import DocumentStatusBadge from './DocumentStatusBadge'
import SignatureBlock from './SignatureBlock'
import VehicleDocumentTable from './VehicleDocumentTable'
import { DOCUMENT_FALLBACKS } from './documentFallbacks'
import { formatAddress, formatDate, formatMoney, formatPhone, safeText, safeVin, shortId } from './documentFormatters'

interface RateConfirmationViewProps {
  load: any
  job?: any
  invoice?: any
}

export default function RateConfirmationView({ load, job, invoice }: RateConfirmationViewProps) {
  const loadRow = load ?? {}
  const jobRow = job ?? {}
  const invoiceRow = invoice ?? null
  const vehicles = Array.isArray(loadRow.vehicles) ? loadRow.vehicles : []
  const carrierPay = Number(
    (typeof loadRow.carrierPayCents === 'number' ? loadRow.carrierPayCents / 100 : undefined)
      ?? loadRow.carrierPay
      ?? loadRow.totalPayment
      ?? 0
  )
  const perVehicle = vehicles.length > 0 && Number.isFinite(carrierPay) ? carrierPay / vehicles.length : null
  const rateLabel = carrierPay > 0 ? formatMoney(perVehicle ?? carrierPay) : DOCUMENT_FALLBACKS.tbd
  const loadNumber = safeText(loadRow.load_number ?? loadRow.loadNumber ?? loadRow.order_number ?? loadRow.orderNumber ?? loadRow.id, DOCUMENT_FALLBACKS.unknown)
  const issuedAt = jobRow.assignedAt ?? loadRow.updated_at ?? loadRow.createdAt ?? loadRow.created_at
  const carrierLabel = safeText(loadRow.carrier_name ?? loadRow.carrierName ?? loadRow.carrier_org_id ?? loadRow.carrierOrgId ?? jobRow.carrier_org_id ?? jobRow.carrierOrgId, DOCUMENT_FALLBACKS.notAssigned)
  const brokerLabel = safeText(loadRow.broker_name ?? loadRow.brokerName ?? loadRow.broker_org_id ?? loadRow.brokerOrgId ?? loadRow.shipper_org_id ?? loadRow.shipperOrgId, DOCUMENT_FALLBACKS.notAssigned)
  const dispatcherLabel = safeText(jobRow.dispatcher_name ?? jobRow.dispatcherName ?? jobRow.dispatched_by ?? jobRow.dispatchedBy ?? 'Diamond Dispatch', 'Diamond Dispatch')

  return (
    <div className="space-y-4 text-slate-900">
      <DocumentSection title="Header" subtitle="Broker-carrier rate confirmation and shipment summary.">
        <DocumentFieldGrid
          items={[
            { label: 'Shipment / Load ID', value: shortId(loadRow.id) },
            { label: 'Load Number', value: loadNumber },
            { label: 'Date Issued', value: formatDate(issuedAt) },
            { label: 'Broker / Company', value: brokerLabel },
            { label: 'Broker Contact', value: formatPhone(loadRow.broker_phone ?? loadRow.brokerPhone ?? loadRow.contact_phone ?? loadRow.contactPhone) },
            { label: 'Carrier', value: carrierLabel },
            { label: 'Dispatcher / Contact', value: dispatcherLabel },
            { label: 'Status', value: <DocumentStatusBadge tone={loadRow.status === 'DELIVERED' ? 'green' : loadRow.status === 'ASSIGNED' ? 'blue' : 'amber'} label={safeText(loadRow.status, 'TBD')} /> },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Route" subtitle="Pickup and delivery details for the movement.">
        <DocumentFieldGrid
          items={[
            { label: 'Pickup Location', value: safeText(loadRow.origin_location ?? loadRow.origin ?? loadRow.pickupLocation, DOCUMENT_FALLBACKS.notProvided) },
            { label: 'Pickup Address', value: formatAddress(loadRow.origin_address ?? loadRow.pickupAddress ?? loadRow.origin) },
            { label: 'Pickup Date / Window', value: safeText(loadRow.pickupDate ?? loadRow.requestedPickup ?? loadRow.pickup_window, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Delivery Location', value: safeText(loadRow.destination_location ?? loadRow.destination ?? loadRow.deliveryLocation, DOCUMENT_FALLBACKS.notProvided) },
            { label: 'Delivery Address', value: formatAddress(loadRow.destination_address ?? loadRow.deliveryAddress ?? loadRow.destination) },
            { label: 'Delivery Date / Window', value: safeText(loadRow.deliveryDate ?? loadRow.requestedDelivery ?? loadRow.delivery_window, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Appointment Status', value: safeText(loadRow.pickupType ?? loadRow.deliveryType ?? loadRow.appointmentStatus ?? 'TBD', 'TBD') },
            { label: 'Reference', value: shortId(jobRow.id ?? loadRow.jobId ?? loadRow.loadId) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Vehicle Items" subtitle={`${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} on this shipment.`}>
        <VehicleDocumentTable
          rows={vehicles}
          columns={[
            { label: 'Unit', render: (vehicle, index) => safeText(vehicle.unitNumber ?? vehicle.unit ?? index + 1, `Unit ${index + 1}`) },
            { label: 'Vehicle', render: (vehicle) => [safeText(vehicle.year, ''), safeText(vehicle.make, ''), safeText(vehicle.model, ''), safeText(vehicle.trim, '')].filter(Boolean).join(' ').trim() || DOCUMENT_FALLBACKS.notProvided },
            { label: 'VIN', render: (vehicle) => safeVin(vehicle.vin) },
            { label: 'Destination', render: () => safeText(loadRow.destination ?? loadRow.destination_location ?? loadRow.destinationAddress, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Notes', render: (vehicle) => safeText(vehicle.notes ?? vehicle.damageNotes ?? vehicle.conditionNotes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          emptyMessage="No vehicle records were attached to this load yet."
        />
      </DocumentSection>

      <DocumentSection title="Rate" subtitle="Commercial payment summary for the carrier.">
        <DocumentFieldGrid
          items={[
            { label: 'Freight Type', value: safeText(loadRow.ship_via ?? loadRow.shipVia ?? loadRow.transportType ?? 'Open Carrier', 'Open Carrier') },
            { label: 'Quantity', value: vehicles.length > 0 ? String(vehicles.length) : DOCUMENT_FALLBACKS.tbd },
            { label: 'Rate', value: rateLabel },
            { label: 'Carrier Pay', value: formatMoney(carrierPay) },
            { label: 'Currency', value: safeText(invoiceRow?.currency ?? loadRow.currency ?? 'USD', 'USD') },
            { label: 'Invoice', value: invoiceRow?.invoice_number ?? invoiceRow?.invoiceNumber ?? DOCUMENT_FALLBACKS.pending },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Payment Terms" subtitle="Paperwork requirements and payment expectations.">
        <DocumentFieldGrid
          items={[
            { label: 'Net Terms', value: safeText(invoiceRow?.dueDate ? 'Net terms apply' : loadRow.paymentTerms ?? 'Net 30', 'Net 30') },
            { label: 'QuickPay Option', value: invoiceRow ? 'Available through accounting review' : DOCUMENT_FALLBACKS.tbd },
            { label: 'Paperwork Required', value: 'Signed rate confirmation, invoice, and POD' },
            { label: 'Invoice Required', value: 'Yes' },
            { label: 'POD Required', value: 'Yes' },
            { label: 'Missing Paperwork Delay', value: 'Payment may be delayed until all required documents are received' },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Instructions" subtitle="Diamond Dispatch-native operating requirements.">
        <ul className="grid gap-2 text-sm text-slate-900 sm:grid-cols-2">
          <li className="rounded border border-slate-200 px-3 py-2">Tracking required while the load is in transit.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Pickup and delivery updates must be reported promptly.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Delays, breakdowns, or exceptions must be escalated immediately.</li>
          <li className="rounded border border-slate-200 px-3 py-2">No unauthorized co-loading if the shipment requires exclusive use.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Vehicle securement and inspection are required prior to departure.</li>
          <li className="rounded border border-slate-200 px-3 py-2">VIN verification is required before release and at delivery.</li>
        </ul>
      </DocumentSection>

      <DocumentSection title="Acceptance">
        <SignatureBlock
          labels={[
            { label: 'Carrier Signature', subtitle: 'Authorized carrier representative' },
            { label: 'Title', subtitle: 'Driver / dispatcher / manager' },
            { label: 'Date', subtitle: 'Signature date' },
          ]}
        />
      </DocumentSection>
    </div>
  )
}
